import { useState, useEffect, useCallback } from "react";
import styles from "./AdminDocuments.module.css";
import { getAdminPerms } from "../../../../lib/adminPerms";
import {
  adminListDocuments,
  adminListFolders,
  adminSyncDocuments,
  adminUploadDocument,
  backendUrl,
  ADMIN_KEY,
} from "../../../../lib/store";

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function OpenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function formatSize(bytes) {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// Cache the last-loaded documents at module scope so switching tabs and coming
// back to Resources shows them instantly (refreshes quietly in the background).
let cachedDocs = null;

const PER_PAGE = 10;

export default function AdminDocuments() {
  const perms = getAdminPerms("documents");
  const [docs, setDocs] = useState(cachedDocs || []);
  const [loading, setLoading] = useState(!cachedDocs);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [showUpload, setShowUpload] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    if (!cachedDocs) setLoading(true);
    setError("");
    adminListDocuments()
      .then((d) => {
        cachedDocs = d;
        setDocs(d);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Pull latest HubSpot docs into the backend, then refresh the list + folders.
  // This only triggers the standalone sync endpoint — no other API is affected.
  const handleSync = async () => {
    setSyncing(true);
    try {
      await adminSyncDocuments();
      load();
    } catch (e) {
      window.alert(e.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const filtered = docs.filter((d) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      d.name.toLowerCase().includes(q) ||
      (d.folder || "").toLowerCase().includes(q)
    );
  });

  // Reset to the first page whenever the search query changes.
  useEffect(() => {
    setPage(1);
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageDocs = filtered.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  );


  return (
    <div className={styles.wrap}>
      <div className={styles.headerRow}>
        <div>
          <div className={styles.pageTitle}>Resources</div>
          <div className={styles.subtle}>Total Resources: {docs.length}</div>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.ghostBtn}
            onClick={handleSync}
            disabled={syncing}
            title="Sync documents from HubSpot"
          >
            {syncing ? "Syncing…" : "⟳ Sync Resources"}
          </button>
          {perms.canAdd && (
            <button className={styles.primaryBtn} onClick={() => setShowUpload(true)}>
              ＋ Upload Resource
            </button>
          )}
        </div>
      </div>

      <input
        className={styles.search}
        placeholder="Search resources..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {loading ? (
        <div className={styles.spinnerWrap}>
          <span className={styles.spinner} />
          <span>Loading resources…</span>
        </div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty}>No resources found.</div>
      ) : (
        <>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.idxCol}>#</th>
                <th>Name</th>
                <th>Folder</th>
                <th>Size</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageDocs.map((d, i) => (
                <tr key={i}>
                  <td className={styles.idxCol}>{(currentPage - 1) * PER_PAGE + i + 1}</td>
                  <td className={styles.fileName}>📄 {d.name}</td>
                  <td>{d.folder || "—"}</td>
                  <td>{formatSize(d.size)}</td>
                  <td className={styles.links}>
                    {(() => {
                      const href = d.localUrl
                        ? backendUrl(d.localUrl)
                        : d.frontendUrl
                          ? d.frontendUrl
                          : d.hubspotId
                            ? backendUrl(
                                `/dev/visioncare/HUBADMFILE?id=${encodeURIComponent(d.hubspotId)}&key=${encodeURIComponent(ADMIN_KEY)}`,
                              )
                            : null;
                      if (!href) return "—";
                      return (
                        <div className={styles.openActions}>
                          <a
                            className={styles.openIcon}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View"
                            aria-label={`View ${d.name}`}
                          >
                            <EyeIcon />
                          </a>
                          <a
                            className={styles.openIcon}
                            href={backendUrl(
                              `/dev/visioncare/HUBADMDL?path=${encodeURIComponent(
                                d.relPath || "",
                              )}&id=${encodeURIComponent(
                                d.hubspotId || "",
                              )}&name=${encodeURIComponent(
                                d.name || "",
                              )}&key=${encodeURIComponent(ADMIN_KEY)}`,
                            )}
                            title="Download"
                            aria-label={`Download ${d.name}`}
                          >
                            <OpenIcon />
                          </a>
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span className={styles.pageInfo}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className={styles.pageBtn}
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next →
            </button>
          </div>
        )}
        </>
      )}

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onDone={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function UploadModal({ onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [topFolder, setTopFolder] = useState("");
  const [subFolder, setSubFolder] = useState("");
  const [newFolder, setNewFolder] = useState("");
  const [creatingNew, setCreatingNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState("");

  // Fetch the HubSpot folders when the modal opens (spinner until they arrive).
  useEffect(() => {
    setFoldersLoading(true);
    adminListFolders()
      .then((list) => {
        setFolders(list);
        const firstTop = (list[0] || "").split("/")[0];
        const firstSub = list.find((f) => f.startsWith(firstTop + "/")) || firstTop;
        setTopFolder(firstTop);
        setSubFolder(firstSub);
      })
      .catch(() => setFolders([]))
      .finally(() => setFoldersLoading(false));
  }, []);

  // Top-level folders, and the subfolders of the currently selected top folder.
  const topFolders = [...new Set(folders.map((f) => f.split("/")[0]))];
  const subFolders = folders.filter((f) => f.startsWith(topFolder + "/"));

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) {
      setErr("Please choose at least one file.");
      return;
    }
    const folder = creatingNew ? newFolder.trim() : subFolder;
    if (creatingNew && !folder) {
      setErr("Please enter the new folder name.");
      return;
    }
    setBusy(true);
    setErr("");
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      setProgress({ current: i + 1, total: files.length });
      try {
        await adminUploadDocument(files[i], folder);
      } catch (ex) {
        failed.push(`${files[i].name}: ${ex.message}`);
      }
    }
    setBusy(false);
    setProgress(null);
    if (failed.length) {
      window.alert(`Some files failed to upload:\n\n${failed.join("\n")}`);
    }
    onDone();
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Upload Resource</div>
        <form onSubmit={submit}>
          {err && <div className={styles.error}>{err}</div>}
          <label className={styles.label}>Files</label>
          <input
            type="file"
            multiple
            accept="application/pdf"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {files.length > 0 && (
            <div className={styles.subtle} style={{ marginTop: 4 }}>
              {files.length} file{files.length > 1 ? "s" : ""} selected
            </div>
          )}
          <label className={styles.label}>Folder</label>
          <div className={styles.folderRow}>
            {foldersLoading ? (
              <div
                className={`${styles.input} ${styles.folderSelect}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#94a3b8",
                }}
              >
                <span className={styles.spinnerSm} /> Loading folders…
              </div>
            ) : (
              <select
                className={`${styles.input} ${styles.folderSelect}`}
                value={topFolder}
                onChange={(e) => {
                  const t = e.target.value;
                  setTopFolder(t);
                  setSubFolder(folders.find((f) => f.startsWith(t + "/")) || t);
                }}
                disabled={creatingNew || folders.length === 0}
              >
                {folders.length === 0 ? (
                  <option value="">No folders yet — use +</option>
                ) : (
                  topFolders.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))
                )}
              </select>
            )}
            <button
              type="button"
              className={`${styles.addFolderBtn} ${creatingNew ? styles.addFolderBtnActive : ""}`}
              onClick={() => setCreatingNew((v) => !v)}
              title={creatingNew ? "Cancel new folder" : "Create new folder"}
              aria-label={creatingNew ? "Cancel new folder" : "Create new folder"}
            >
              {creatingNew ? "×" : "+"}
            </button>
          </div>

          {/* Subfolder dropdown — only when the chosen top folder has subfolders */}
          {!creatingNew && !foldersLoading && subFolders.length > 0 && (
            <select
              className={styles.input}
              style={{ marginTop: 8 }}
              value={subFolder}
              onChange={(e) => setSubFolder(e.target.value)}
            >
              {subFolders.map((s) => (
                <option key={s} value={s}>
                  {s.slice(topFolder.length + 1)}
                </option>
              ))}
            </select>
          )}

          {creatingNew && (
            <input
              className={styles.input}
              style={{ marginTop: 8 }}
              placeholder="New folder, e.g. Optometrist/Guides"
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              autoFocus
            />
          )}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.ghostBtn}
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button type="submit" className={styles.primaryBtn} disabled={busy}>
              {busy
                ? progress
                  ? `Uploading ${progress.current}/${progress.total}…`
                  : "Uploading…"
                : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
